import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { translateMuscle } from '../../lib/muscleTranslations';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';

import { toast } from 'sonner';
import {
  Dumbbell, ArrowLeft, ChevronRight, ChevronDown, ChevronUp, Plus, Trash2, Pencil, X, Save,
  Search, Copy, GripVertical, FileDown, ArrowUpDown, Loader2, Info, Calendar, MessageCircle
} from 'lucide-react';
import TechniqueCueEditor from '../TechniqueCueEditor';
import client from '../../api/client';
import { useTranslation } from 'react-i18next';
import ExercisePicker from '../ExercisePicker';

import TargetMuscleBadges from '../TargetMuscleBadges';
import { MuscleDetailModal } from '../MuscleBodyMap';
import { getTargetMuscleStats, TARGET_MUSCLE_COLORS } from '../../lib/muscleUtils';
import { MUSCLE_GROUPS } from '../../lib/constants';
import WeekPlannerModal from '../WeekPlannerModal';
import { getExerciseKey } from '../../utils/exerciseKey';
import { useAutosaveEditor } from '../../hooks/useAutosaveEditor';

import { moveDayInList, moveExerciseInDay, moveExerciseAcrossDays } from './routineBoard/routineMutations';
import RoutineBoard from './routineBoard/RoutineBoard';
import ExerciseDetailModal from '../ExerciseDetailModal';

// ==================== EXPORTS ====================
export const MUSCLE_GROUP_COLORS_MAP = TARGET_MUSCLE_COLORS;

export function getRoutineWorkSets(days, exerciseMuscleMap = null) {
  return getTargetMuscleStats(days, true, exerciseMuscleMap);
}

export function WorkSetsTags({ stats, size = 'sm' }) {
  return <TargetMuscleBadges stats={stats} size={size} />;
}

export function RoutineEditor({ routine, clientUser, onSave, onSaveAsTemplate, showNameEditor, onClose, initialDayIdx = null }) {
  const { t, i18n } = useTranslation();
  const { language } = useLanguage();
  const { user } = useAuth();
  const isCoachOrAdmin = user?.role === 'coach' || user?.role === 'admin';
  const isPt = i18n.language === 'pt';
  const {
    document: editedRoutine,
    update: applyUpdate,
    undo, redo, canUndo, canRedo,
    status: saveStatus,
    flush: flushSave,
  } = useAutosaveEditor({
    initialDocument: routine,
    save: async (doc, { sessionId }) => { await onSave(doc, { sessionId }); },
  });
  const hasChanges = saveStatus === 'dirty' || saveStatus === 'saving' || saveStatus === 'error';
  const [deleteExerciseConfirm, setDeleteExerciseConfirm] = useState(null); // { dayIdx, exIdx } | null
  const [showExercisePicker, setShowExercisePicker] = useState(false);
  const [pickerDayIdx, setPickerDayIdx] = useState(null);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [exerciseResults, setExerciseResults] = useState([]);
  const [exerciseMuscleFilter, setExerciseMuscleFilter] = useState('');
  const [exerciseEquipmentFilter, setExerciseEquipmentFilter] = useState('');  const [showCustomExercise, setShowCustomExercise] = useState(false);
  const [customExercise, setCustomExercise] = useState({ name: '', muscle_group: 'Chest', primary_muscles: '', secondary_muscles: '', equipment: '' });
  // Technique cue editor â€” { exerciseId, exerciseName } when open
  const [cueEditor, setCueEditor] = useState(null);
  // Board: which exercise's set-editor modal is open
  // Exchange exercise â€” { dayIdx, exIdx } | null
  const [exchangeTarget, setExchangeTarget] = useState(null);
  const [exchangeSearch, setExchangeSearch] = useState('');
  const [exchangeResults, setExchangeResults] = useState([]);
  const [exchangeMuscleFilter, setExchangeMuscleFilter] = useState('');
  // Exercise preview
  const [previewExercise, setPreviewExercise] = useState(null);
  // Exercise image lookup
  const [exerciseImages, setExerciseImages] = useState({});
  const [exerciseMuscleMap, setExerciseMuscleMap] = useState({}); // name -> { targetMuscles, secondaryMuscles }
  const [exerciseDbReady, setExerciseDbReady] = useState(false);
  const [pendingDeleteDayIdx, setPendingDeleteDayIdx] = useState(null);
  // Muscle detail modal
  const [muscleModalStats, setMuscleModalStats] = useState(null);
  // Week Planner
  const [weekPlannerOpen, setWeekPlannerOpen] = useState(false);
  const [routineWithSchedule, setRoutineWithSchedule] = useState(routine);
  // Import Day from another routine
  const [showImportDay, setShowImportDay] = useState(false);
  const [allRoutines, setAllRoutines] = useState([]);
  const [selectedImportRoutine, setSelectedImportRoutine] = useState(null);
  const [loadingRoutines, setLoadingRoutines] = useState(false);
  const [importSearch, setImportSearch] = useState('');

  const EQUIPMENT_LIST = ['Barbell','Dumbbell','Cable','Machine','Bodyweight','Smith Machine','EZ-Bar','Plate'];
  const SET_TYPE_COLORS = {
    warmup: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
    work: 'bg-primary/10 text-primary border-primary/30',
    drop: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    backoff: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };

  useEffect(() => {
    if (routine) {
      // Pre-populate exerciseImages from the routine's own stored data
      const map = {};
      routine.days?.forEach(day => {
        day.exercises?.forEach(ex => {
          const url = ex.gifUrl || ex.image_url;
          if (url && ex.name) map[ex.name] = url;
        });
      });
      if (Object.keys(map).length > 0) setExerciseImages(prev => ({ ...map, ...prev }));
    }
  }, [routine]);

  useEffect(() => {
    setRoutineWithSchedule(routine);
  }, [routine]);

  // Load exercise images and muscle data on mount
  useEffect(() => {
    const loadImages = async () => {
      try {
        const res = await client.get('/exercises?limit=2000');
        const imgMap = {};
        const muscleMap = {};
        res.data.forEach(ex => {
          const url = ex.gifUrl || ex.image_url;
          // Only write if we have a URL â€” don't clobber anything already in the map
          // with null. Also lets a routine's own stored URL survive when the DB
          // entry is missing an image.
          if (url && !imgMap[ex.name]) imgMap[ex.name] = url;
          if (ex.name) muscleMap[ex.name] = {
            targetMuscles: ex.targetMuscles || [],
            secondaryMuscles: ex.secondaryMuscles || [],
          };
        });
        // Merge with any URLs already pre-populated from the routine itself.
        // The live DB map takes precedence (it's canonical after dedupe), but
        // anything only present in the routine snapshot (custom exercises, etc.)
        // is preserved instead of wiped.
        setExerciseImages(prev => ({ ...prev, ...imgMap }));
        setExerciseMuscleMap(muscleMap);
        setExerciseDbReady(true);
      } catch {
        setExerciseDbReady(true);
      }
    };
    loadImages();
  }, []);




  const r = editedRoutine || routine;
  if (!r) return null;

  const modify = (fn) => { applyUpdate((copy) => { fn(copy); }); };

  const loadExercises = async (search = '', muscle = '', equipment = '') => {
    try {
      let url = `/exercises?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (muscle) url += `muscle_group=${encodeURIComponent(muscle)}&`;
      if (equipment) url += `equipment=${encodeURIComponent(equipment)}`;
      const res = await client.get(url);
      setExerciseResults(res.data);
    } catch (err) { toast.error(err?.response?.data?.detail || t('routine.failedLoadExercises')); }
  };

  // Load all routines for import day feature
  const loadAllRoutines = async () => {
    setLoadingRoutines(true);
    setImportSearch('');
    try {
      const res = await client.get('/routines/all-for-import');
      setAllRoutines(res.data || []);
    } catch {
      toast.error(t('routine.failedLoadRoutines'));
    } finally {
      setLoadingRoutines(false);
    }
  };

  // Import a day from another routine
  const importDay = (sourceDay) => {
    const newDay = {
      id: Math.random().toString(36).substr(2, 9),
      day_name: `${sourceDay.day_name} (Copy)`,
      exercises: sourceDay.exercises.map(ex => ({
        ...ex,
        id: Math.random().toString(36).substr(2, 9),
        sets: ex.sets?.map(s => ({ ...s, id: Math.random().toString(36).substr(2, 9) })) || []
      }))
    };
    modify(c => { c.days.push(newDay); });
    setShowImportDay(false);
    setSelectedImportRoutine(null);
    toast.success(t('routine.importedDay', { name: sourceDay.day_name }));
  };

  const addExerciseToDayIdx = (dayIdx, exercise) => {
    if (dayIdx === null || dayIdx === undefined || !r.days?.[dayIdx]) return;
    const snapshotUrl = exercise.gifUrl || exercise.image_url || null;
    modify(c => {
      c.days[dayIdx].exercises.push({
        id: Math.random().toString(36).substr(2, 9),
        name: exercise.name,
        muscle_group: exercise.muscle_group,
        targetMuscles: exercise.targetMuscles || [],
        secondaryMuscles: exercise.secondaryMuscles || [],
        gifUrl: snapshotUrl,
        image_url: snapshotUrl,
        notes: '',
        sets: [{ set_number: 1, set_type: 'work', target_reps_min: 8, target_reps_max: 12, target_weight: 0 }]
      });
    });
    if (snapshotUrl) setExerciseImages(prev => ({ ...prev, [exercise.name]: snapshotUrl }));
    setShowExercisePicker(false);
    setExerciseSearch('');
  };

  const addCustomExercise = async () => {
    try {
      const payload = {
        ...customExercise,
        primary_muscles: customExercise.primary_muscles.split(',').map(s => s.trim()).filter(Boolean),
        secondary_muscles: customExercise.secondary_muscles.split(',').map(s => s.trim()).filter(Boolean),
      };
      const res = await client.post('/exercises', payload);
      toast.success(t('routine.exerciseAdded'));
      addExerciseToDayIdx(pickerDayIdx, res.data);
      setShowCustomExercise(false);
      setCustomExercise({ name: '', muscle_group: 'Chest', primary_muscles: '', secondary_muscles: '', equipment: '' });
    } catch { toast.error(t('routine.exerciseAddFailed')); }
  };

  const MUSCLE_GROUP_PT_TO_EN = {
    'Peito': 'Chest', 'Costas': 'Back', 'Ombros': 'Shoulders',
    'Bíceps': 'Upper Arms', 'Tríceps': 'Upper Arms', 'Braços': 'Upper Arms',
    'Quadríceps': 'Upper Legs', 'Isquiotibiais': 'Upper Legs',
    'Glúteos': 'Upper Legs', 'Pernas': 'Upper Legs',
    'Core': 'Waist', 'Abdominais': 'Waist',
    'Gémeos': 'Lower Legs', 'Panturrilhas': 'Lower Legs',
    'Antebraços': 'Lower Arms',
    'Pescoço': 'Neck', 'Cardio': 'Cardio',
  };

  // Exchange exercise - load with same muscle group first
  const openExchangeModalForDay = async (dayIdx, exIdx) => {
    const exercise = r.days[dayIdx].exercises[exIdx];
    setExchangeTarget({ dayIdx, exIdx });
    const rawMG = exercise.muscle_group || '';
    const enMG = MUSCLE_GROUP_PT_TO_EN[rawMG] || rawMG;
    setExchangeMuscleFilter(enMG);
    setExchangeSearch('');
    try {
      let url = `/exercises?`;
      if (enMG) url += `muscle_group=${encodeURIComponent(enMG)}`;
      const res = await client.get(url);
      setExchangeResults(res.data.filter(e => e.name !== exercise.name));
    } catch { setExchangeResults([]); }
  };

  const loadExchangeExercises = async (search = '', muscle = '') => {
    try {
      let url = `/exercises?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (muscle) url += `muscle_group=${encodeURIComponent(muscle)}`;
      const res = await client.get(url);
      const currentName = exchangeTarget ? r.days[exchangeTarget.dayIdx]?.exercises[exchangeTarget.exIdx]?.name : '';
      setExchangeResults(res.data.filter(e => e.name !== currentName));
    } catch { setExchangeResults([]); }
  };

  const confirmExchange = (newExercise) => {
    if (!exchangeTarget) return;
    const { dayIdx, exIdx } = exchangeTarget;
    modify(c => {
      const oldEx = c.days[dayIdx].exercises[exIdx];
      oldEx.name = newExercise.name;
      oldEx.muscle_group = newExercise.muscle_group;
      oldEx.gifUrl = newExercise.gifUrl || null;
      oldEx.image_url = newExercise.image_url || null;
      oldEx.exerciseId = newExercise.exerciseId || newExercise.id || null;
      oldEx.primary_muscles = newExercise.primary_muscles || newExercise.targetMuscles || [];
      oldEx.secondary_muscles = newExercise.secondary_muscles || newExercise.secondaryMuscles || [];
      oldEx.equipment = newExercise.equipment || null;
    });
    if (newExercise.image_url || newExercise.gifUrl) {
      setExerciseImages(prev => ({ ...prev, [newExercise.name]: newExercise.gifUrl || newExercise.image_url }));
    }
    setExchangeTarget(null);
    toast.success(t('routine.swappedTo', { name: newExercise.name }));
  };

  // Duplicate a day
  const duplicateDay = (dayIdx) => {
    const sourceDay = r.days[dayIdx];
    const newDay = {
      id: Math.random().toString(36).substr(2, 9),
      day_name: `${sourceDay.day_name} (Copy)`,
      exercises: sourceDay.exercises.map(ex => ({
        ...ex,
        id: Math.random().toString(36).substr(2, 9),
        sets: ex.sets?.map(s => ({ ...s, id: Math.random().toString(36).substr(2, 9) })) || []
      }))
    };
    modify(c => { c.days.splice(dayIdx + 1, 0, newDay); });
    toast.success(t('routine.duplicatedDay', { name: sourceDay.day_name }));
  };

  // NOTE: getTargetMuscleStats now uses `exerciseMuscleMap` directly as a live
  // override, so we no longer need to pre-enrich the days before computing stats.
  // The map is the single source of truth â€” routine document values are only
  // used as a fallback for custom exercises not in the central ExerciseDB.
  // (The old `enrichDays` helper was removed because it only overrode fields
  // and still left day-level stats computing from raw selectedDay.)

  // â”€â”€â”€ DAY BOARD VIEW â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const { total: routineWorkSetsTotal } = getRoutineWorkSets(r.days, exerciseMuscleMap);
  const totalWorkSetsNum = Object.values(routineWorkSetsTotal).reduce((a, b) => a + b, 0);

  // â”€â”€ Board drag handlers (each one atomic applyUpdate = one undo step) â”€â”€
  const onReorderDay = (from, to) => modify(c => moveDayInList(c.days, from, to));
  const onReorderExercise = (dayIdx, from, to) => modify(c => moveExerciseInDay(c.days, dayIdx, from, to));
  const onMoveExerciseAcrossDays = (fd, fe, td, te) => modify(c => moveExerciseAcrossDays(c.days, fd, fe, td, te));

  const onRenameDay = (dayIdx, name) => modify(c => { c.days[dayIdx].day_name = name; });
  const onRequestDeleteDay = (dayIdx) => {
    if (r.days.length <= 1) return;
    if ((r.days[dayIdx].exercises?.length || 0) > 0) setPendingDeleteDayIdx(dayIdx);
    else modify(c => { c.days.splice(dayIdx, 1); });
  };
  const onAddDay = () => modify(c => { c.days.push({ id: Math.random().toString(36).substr(2, 9), day_name: `Day ${c.days.length + 1}`, exercises: [] }); });
  const onAddExerciseForDay = (dayIdx) => { setPickerDayIdx(dayIdx); setShowExercisePicker(true); loadExercises('', '', ''); };

  const getDayStats = (dayIdx) => getTargetMuscleStats([r.days[dayIdx]], true, exerciseMuscleMap).total;

  // Per-card callbacks (parent closes over dayIdx/exIdx).
  const cardCallbacks = (dayIdx, exIdx, ex) => ({
    setTypeColors: SET_TYPE_COLORS,
    onInfo: () => setPreviewExercise(ex),
    onExchange: () => openExchangeModalForDay(dayIdx, exIdx),
    onEditCue: () => setCueEditor({ exerciseId: getExerciseKey(ex), exerciseName: ex.name }),
    onDelete: () => setDeleteExerciseConfirm({ dayIdx, exIdx }),
    // Inline set editor mutations (bound to this card's day/exercise index)
    onAddSet: () => modify(c => {
      const sets = c.days[dayIdx].exercises[exIdx].sets;
      const last = sets[sets.length - 1] || {};
      sets.push({ set_number: sets.length + 1, set_type: 'work', target_reps_min: last.target_reps_min || 8, target_reps_max: last.target_reps_max || 12, target_weight: last.target_weight || last.weight || 0 });
    }),
    onRemoveSet: (setIdx) => modify(c => { c.days[dayIdx].exercises[exIdx].sets.splice(setIdx, 1); }),
    onChangeSetType: (setIdx, val) => modify(c => { c.days[dayIdx].exercises[exIdx].sets[setIdx].set_type = val; }),
    onChangeField: (setIdx, field, val) => modify(c => { c.days[dayIdx].exercises[exIdx].sets[setIdx][field] = val; }),
  });

  return (
    <div data-testid="routine-editor" className="flex flex-col h-full">
      <RoutineBoard
        days={r.days || []}
        totalStats={routineWorkSetsTotal}
        totalCount={totalWorkSetsNum}
        getDayStats={getDayStats}
        exerciseDbReady={exerciseDbReady}
        language={language}
        exerciseMuscleMap={exerciseMuscleMap}
        exerciseImages={exerciseImages}
        isCoachOrAdmin={isCoachOrAdmin}
        showNameEditor={showNameEditor}
        routineName={editedRoutine?.name}
        routineDescription={editedRoutine?.description}
        onChangeName={(v) => modify(c => { c.name = v; })}
        onChangeDescription={(v) => modify(c => { c.description = v; })}
        onAddDay={onAddDay}
        onImportDay={isCoachOrAdmin ? () => { loadAllRoutines(); setShowImportDay(true); } : undefined}
        onSaveAsTemplate={onSaveAsTemplate}
        onWeekPlanner={() => setWeekPlannerOpen(true)}
        onClose={onClose ? async () => { try { await flushSave(); onClose?.(); } catch { toast.error(t('autosave.saveFailedStayOpen', 'Could not save — still trying. Your changes are kept.')); } } : undefined}
        onRenameDay={onRenameDay}
        onDuplicateDay={duplicateDay}
        onRequestDeleteDay={onRequestDeleteDay}
        onAddExercise={onAddExerciseForDay}
        onOpenMuscleDetail={(s) => setMuscleModalStats(s)}
        cardCallbacks={cardCallbacks}
        onReorderDay={onReorderDay}
        onReorderExercise={onReorderExercise}
        onMoveExerciseAcrossDays={onMoveExerciseAcrossDays}
        autosave={{ canUndo, canRedo, onUndo: undo, onRedo: redo, status: saveStatus }}
      />

      {/* Add Exercise picker */}
      <ExercisePicker
        open={showExercisePicker}
        onClose={() => { setShowExercisePicker(false); setPickerDayIdx(null); }}
        onSelect={(ex) => addExerciseToDayIdx(pickerDayIdx, ex)}
        title={t('routineEditor.addExercise')}
      />

      {/* Exchange picker */}
      <ExercisePicker
        open={!!exchangeTarget}
        onClose={() => { setExchangeTarget(null); setExchangeSearch(''); setExchangeMuscleFilter(''); }}
        onSelect={(ex) => confirmExchange(ex)}
        title={t('routineEditor.exchangeExercise')}
        replacingExercise={exchangeTarget ? r.days?.[exchangeTarget.dayIdx]?.exercises?.[exchangeTarget.exIdx] : null}
      />

      {/* Exercise Preview — full-screen modal shared with the workout flow
          (GIF/video, muscle tags, personal history, instructions). Fetches its
          own details + history via react-query and self-heals dead media URLs. */}
      <ExerciseDetailModal
        exercise={previewExercise}
        open={!!previewExercise}
        onClose={() => setPreviewExercise(null)}
        historyUserId={clientUser?.id || null}
      />

      {/* Custom Exercise Dialog */}
      <Dialog open={showCustomExercise} onOpenChange={setShowCustomExercise}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('routineEditor.addCustomExercise')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>{t('routineEditor.exerciseName')}</Label><Input value={customExercise.name} onChange={(e) => setCustomExercise(p => ({ ...p, name: e.target.value }))} placeholder={t('routineEditor.exerciseNamePlaceholder')} data-testid="custom-exercise-name" /></div>
            <div className="space-y-2"><Label>{t('routineEditor.muscleGroup')}</Label>
              <Select value={customExercise.muscle_group} onValueChange={(v) => setCustomExercise(p => ({ ...p, muscle_group: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MUSCLE_GROUPS.map(mg => <SelectItem key={mg} value={mg}>{translateMuscle(mg, language)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>{t('routineEditor.primaryMuscles')}</Label><Input value={customExercise.primary_muscles} onChange={(e) => setCustomExercise(p => ({ ...p, primary_muscles: e.target.value }))} placeholder={t('routineEditor.primaryMusclesPlaceholder')} /></div>
            <div className="space-y-2"><Label>{t('routineEditor.equipmentLabel')}</Label><Input value={customExercise.equipment} onChange={(e) => setCustomExercise(p => ({ ...p, equipment: e.target.value }))} placeholder={t('routineEditor.equipmentPlaceholder')} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomExercise(false)}>{t('common.cancel')}</Button>
            <Button onClick={addCustomExercise} className="bg-primary hover:bg-primary/90" disabled={!customExercise.name.trim()} data-testid="save-custom-exercise-btn">{t('clients.addExercise')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Day Dialog */}
      <Dialog open={showImportDay} onOpenChange={(open) => { setShowImportDay(open); if (!open) { setSelectedImportRoutine(null); setImportSearch(''); } }}>
        <DialogContent className="max-w-lg max-h-[85dvh] flex flex-col">
          <DialogHeader className="pb-0">
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-base">
              {selectedImportRoutine ? (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7" tooltip={t('tooltips.back')} onClick={() => { setSelectedImportRoutine(null); setImportSearch(''); }}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="min-w-0">
                    <span className="truncate block">{selectedImportRoutine.name}</span>
                    <span className="text-xs font-normal text-muted-foreground">{t('routineEditor.daysCount', { count: selectedImportRoutine.days?.length || 0 })}</span>
                  </div>
                </div>
              ) : (
                <>{t('routineEditor.importTrainingDay')}</>
              )}
            </DialogTitle>
          </DialogHeader>

          {!selectedImportRoutine && !loadingRoutines && (
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('routineEditor.searchRoutines')}
                value={importSearch}
                onChange={(e) => setImportSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
                data-testid="import-search-input"
              />
            </div>
          )}

          <div className="flex-1 min-h-0 mt-2">
            {loadingRoutines ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : selectedImportRoutine ? (
              <ScrollArea className="h-[min(380px,60dvh)]">
                <div className="space-y-2 pr-3">
                  {selectedImportRoutine.days?.map((day, idx) => {
                    const exerciseCount = day.exercises?.length || 0;
                    const setCount = day.exercises?.reduce((acc, ex) => acc + (ex.sets?.length || 0), 0) || 0;
                    const exerciseNames = day.exercises?.slice(0, 3).map(ex => ex.name).join(', ') || '';
                    const moreCount = exerciseCount > 3 ? exerciseCount - 3 : 0;
                    return (
                      <div
                        key={day.id || idx}
                        className="p-3 rounded-xl border border-border/50 hover:border-primary/50 hover:bg-primary/90 cursor-pointer transition-all group"
                        onClick={() => importDay(day)}
                        data-testid={`import-day-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{day.day_name}</p>
                            <p className="text-xs text-muted-foreground">{exerciseCount} {t('routineEditor.exercises')} · {setCount} {t('routineEditor.sets')}</p>
                            {exerciseNames && (
                              <p className="text-xs text-muted-foreground/60 mt-0.5 truncate">
                                {exerciseNames}{moreCount > 0 ? ` ${t('routineEditor.moreSuffix', { count: moreCount })}` : ''}
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0 w-7 h-7 rounded-lg border border-dashed border-muted-foreground/30 group-hover:border-primary/50 flex items-center justify-center transition-colors">
                            <Plus className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(!selectedImportRoutine.days || selectedImportRoutine.days.length === 0) && (
                    <p className="text-center text-muted-foreground py-8 text-sm">{t('routineEditor.noDaysInRoutine')}</p>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <ScrollArea className="h-[min(380px,60dvh)]">
                <div className="space-y-2 pr-3">
                  {(() => {
                    const filtered = allRoutines
                      .filter(rt => rt.id !== routine?.id)
                      .filter(rt => !importSearch || rt.name.toLowerCase().includes(importSearch.toLowerCase()));
                    if (filtered.length === 0) {
                      return <p className="text-center text-muted-foreground py-8 text-sm">{importSearch ? t('routineEditor.noRoutinesMatch') : t('routineEditor.noOtherRoutines')}</p>;
                    }
                    return filtered.map((rt, idx) => {
                      const totalExercises = rt.days?.reduce((acc, d) => acc + (d.exercises?.length || 0), 0) || 0;
                      const isClientRoutine = !!rt.client_id;
                      return (
                        <div
                          key={rt.id || idx}
                          className="p-3 rounded-xl border border-border/50 hover:border-primary/50 hover:bg-primary/90 cursor-pointer transition-all group"
                          onClick={() => setSelectedImportRoutine(rt)}
                          data-testid={`select-routine-${idx}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                              <Dumbbell className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm truncate">{rt.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {rt.days?.length || 0} days · {totalExercises} exercises
                                {isClientRoutine && <span className="ml-1 text-primary/70">({t('routineEditor.clientLabel')})</span>}
                                {!isClientRoutine && <span className="ml-1 text-emerald-500/70">({t('routineEditor.templateLabel')})</span>}
                              </p>
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </ScrollArea>
            )}
          </div>

          <DialogFooter className="pt-2 border-t border-border/50">
            <Button variant="outline" size="sm" onClick={() => { setShowImportDay(false); setSelectedImportRoutine(null); setImportSearch(''); }}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Week Planner */}
      <WeekPlannerModal
        open={weekPlannerOpen}
        onClose={() => setWeekPlannerOpen(false)}
        routine={routineWithSchedule}
        isPersonal={!!routineWithSchedule?.is_personal}
        onSaved={(scheduleData) => {
          setRoutineWithSchedule(prev => prev ? { ...prev, ...scheduleData } : prev);
          applyUpdate((d) => { Object.assign(d, scheduleData); });
        }}
      />

      {/* Muscle detail modal */}
      <MuscleDetailModal
        muscleStats={muscleModalStats || {}}
        isOpen={!!muscleModalStats}
        onClose={() => setMuscleModalStats(null)}
        routineName={editedRoutine?.name}
      />

      {/* Delete-day confirm */}
      <AlertDialog open={pendingDeleteDayIdx !== null} onOpenChange={(open) => { if (!open) setPendingDeleteDayIdx(null); }}>
        <AlertDialogContent data-testid="delete-day-card-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('routineEditor.deleteDay')}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteDayIdx !== null && t('routineEditor.deleteDayDesc', { name: r.days?.[pendingDeleteDayIdx]?.day_name || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-day-card-cancel-btn">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90"
              data-testid="delete-day-card-confirm-btn"
              onClick={() => { if (pendingDeleteDayIdx !== null) modify(c => { c.days.splice(pendingDeleteDayIdx, 1); }); setPendingDeleteDayIdx(null); }}>
              {t('routineEditor.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete-exercise confirm â€” reads {dayIdx, exIdx} */}
      <AlertDialog open={deleteExerciseConfirm !== null} onOpenChange={(open) => { if (!open) setDeleteExerciseConfirm(null); }}>
        <AlertDialogContent data-testid="delete-exercise-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('routineEditor.removeExercise')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('routineEditor.removeExerciseDesc', { name: deleteExerciseConfirm ? editedRoutine?.days?.[deleteExerciseConfirm.dayIdx]?.exercises?.[deleteExerciseConfirm.exIdx]?.name : '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-exercise-cancel-btn">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" data-testid="delete-exercise-confirm-btn"
              onClick={() => { if (deleteExerciseConfirm) modify(c => { c.days[deleteExerciseConfirm.dayIdx].exercises.splice(deleteExerciseConfirm.exIdx, 1); }); setDeleteExerciseConfirm(null); }}>
              {t('routineEditor.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cue editor */}
      {isCoachOrAdmin && cueEditor && (
        <TechniqueCueEditor
          open onOpenChange={(v) => !v && setCueEditor(null)}
          exerciseId={cueEditor.exerciseId}
          exerciseName={cueEditor.exerciseName}
          clientId={clientUser?.id}
          clientName={clientUser?.name}
        />
      )}
    </div>
  );
}
